import {
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
} from 'react-aria-components'
import type { ReactNode } from 'react'
import { Button } from '../inputs/Button'
import { Icon } from '../icons/Icon'
import { X } from '@bloo-team/ui/icons'

export interface SlideOverProps {
  open: boolean
  onClose: () => void
  title: string
  /** Mono eyebrow above the title (entity type, id). */
  eyebrow?: string
  children?: ReactNode
  /** Footer actions, usually pivots. */
  actions?: ReactNode
  /** Widen the sheet (signal deep panel) — `.app-slideover[data-wide]`. */
  wide?: boolean
  /**
   * Require an explicit close: a backdrop click and Escape stop dismissing.
   *
   * For panels where dismissing DESTROYS something unrecoverable — the
   * one-time token secret (docs/settings-requirements.md §3.4) is the case
   * this exists for. Off by default: everywhere else, an easy escape is the
   * right behaviour and this would only trap people.
   */
  requireExplicitClose?: boolean
}

/**
 * The detail slide-over (docs/IA.md IA5): entities open beside the list, not
 * instead of it — list scroll and filters survive. React Aria supplies focus
 * trap, Escape, and focus restore to the row that opened it.
 */
export function SlideOver({
  open, onClose, title, eyebrow, children, actions, wide, requireExplicitClose = false,
}: SlideOverProps) {
  return (
    <ModalOverlay
      isOpen={open}
      onOpenChange={(v) => { if (!v) onClose() }}
      // Both are needed: isDismissable governs the backdrop click, and
      // Escape has its own flag. Leaving either on defeats the other.
      isDismissable={!requireExplicitClose}
      isKeyboardDismissDisabled={requireExplicitClose}
      className="app-slideover-backdrop"
    >
      <Modal className="app-slideover" data-wide={wide ? '' : undefined}>
        <AriaDialog className="app-slideover-dialog" aria-label={title}>
          <header className="app-slideover-head">
            <div className="app-slideover-titles">
              {eyebrow ? <span className="bui-eyebrow">{eyebrow}</span> : null}
              <Heading slot="title" className="app-slideover-title">{title}</Heading>
            </div>
            <Button variant="ghost" size="sm" aria-label="Close" onPress={onClose}>
              <Icon icon={X} size={15} />
            </Button>
          </header>
          <div className="app-slideover-body">{children}</div>
          {actions ? <footer className="app-slideover-foot">{actions}</footer> : null}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  )
}
